import isNil from 'lodash/isNil';
import {
	Observable,
	type GraphQLSingularResponse,
	type RequestParameters,
	type Variables,
	type GraphQLResponse,
	type CacheConfig,
} from 'relay-runtime';
import { fg } from '@atlassian/jira-feature-gating';
import { retryOnError } from '@atlassian/jira-fetch/src/utils/retries.tsx';
import { captureErrors, isUnauthenticated } from '@atlassian/jira-relay-errors/src/index.tsx';
import {
	QueryPromisesMap,
	CompletedQueryPromisesMap,
} from '@atlassian/jira-relay-query-promises/src/index.tsx';
import { getQueryResponseCache } from '@atlassian/jira-relay-query-responses/src/index.tsx';
import {
	claimEarlyScriptPromise,
	getEarlyScriptPromiseKey,
} from '@atlassian/jira-relay-vendorless-utils/src/services/early-script-promise/index.tsx';
import { putExtensions } from '@atlassian/relay-capture-extensions';
import { getAggEndpoint, getRequestBody } from '@atlassian/relay-endpoint';
import { addTraceId } from '@atlassian/relay-traceid';
import { registerEvent } from '@atlassian/jira-s2c-server-receiver/src/index.tsx';
import { getClientDispatcher } from '@atlassian/jira-s2c-client-dispatcher/src/singleton.tsx';
import HttpError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import {
	buildGraphqlFetch,
	processMultipartPromise,
	withErrorHandling,
} from './services/multipart-fetch/index.tsx';
import { showUnauthenticatedFlag } from './services/error-flags/index.tsx';
import { getLoggingAndMetricsHelper } from './services/logging-and-metrics/index.tsx';
import {
	checkAuthentication,
	getTraceIdFromExtensions,
	blocklist,
	generateOptions,
	shouldRetry,
	getRetryAttemptsForQuery,
} from './utils.tsx';
import type { GraphQLMultipartResponsePayload } from './services/multipart-fetch/types.tsx';

// @ts-expect-error the global is placed there by bifrost SSR. See src/entry-fs/util/render.tsx
const isSsrHandoverIncrementsEnabled = () => Boolean(globalThis.__SSR_HANDOVER_INCREMENTS);

const moduleID = 'relay-fetch';

export const fetch = (
	request: RequestParameters,
	variables: Variables,
	cacheConfig: CacheConfig,
): Observable<GraphQLResponse> => {
	if (!fg('remove_heartbeat_service_from_jira_relay_fetch')) {
		const redirecting = checkAuthentication();
		if (redirecting) {
			// Hang the request if we are redirecting - should be cleared on redirect
			// eslint-disable-next-line @typescript-eslint/no-empty-function
			return Observable.from(new Promise<GraphQLResponse>(() => {}));
		}
	}

	const { name: queryName, operationKind } = request;
	const loggingMetadata = {
		queryName,
		operationKind,
	};
	const loggingAndMetrics = getLoggingAndMetricsHelper(loggingMetadata);

	function augmentResponse(response: GraphQLSingularResponse) {
		addTraceId(queryName, getTraceIdFromExtensions(response));
		putExtensions(queryName, response.extensions);
	}

	function validateResponse(response: GraphQLSingularResponse) {
		// @ts-expect-error - TS2339 - Property 'errors' does not exist on type 'GraphQLSingularResponse'.
		const errors = response.errors || undefined;
		if (errors != null) {
			const requestId = response?.extensions?.gateway?.request_id;
			if (!blocklist.includes(queryName)) {
				loggingAndMetrics.logNonBreakingErrors(errors, requestId);
			}
			captureErrors(errors, { operationName: queryName, requestId });

			if (isUnauthenticated(errors)) {
				return false;
			}
		}
		return true;
	}

	function enforceNonNullability(response: GraphQLSingularResponse) {
		if (response.data && (response.data.jira === null || response.data.customerService === null)) {
			// Enforce non-nullability of the jira and customerService namespaces. This is an intermediary solution to
			// prevent server errors/gateway timeouts from wiping the Relay store. Eventually we'd like this
			// to be enforced at a schema level, see further discussion https://hello.atlassian.net/wiki/spaces/~236031707/pages/2034382845/Top+level+errors+for+namespaces+in+AGG+Relay
			// @ts-expect-error - TS2322 - Relay types do not allow null data for a response with errors, however this is valid according to the spec http://spec.graphql.org/June2018/#sec-Data
			response.data = null;
		}
	}

	const earlyScriptPromise =
		(!__SERVER__ && claimEarlyScriptPromise(getEarlyScriptPromiseKey(request, variables))) ||
		undefined;

	return Observable.create((sink) => {
		try {
			const requestID =
				'cacheID' in request && request.cacheID != null ? request.cacheID : request.id;
			// Unique identifier for the server-to-client channel.
			const sinkID = `${moduleID}-${requestID}`;
			let resolveCompletedResponse: () => void;
			const completedResponse: Promise<void> = new Promise((resolve) => {
				resolveCompletedResponse = resolve;
			});

			const finalise = () => {
				resolveCompletedResponse();
				sink.complete();
			};

			const handleResponse = (response: GraphQLMultipartResponsePayload) => {
				let payload: GraphQLSingularResponse | null = null;
				if ('incremental' in response && response.incremental) {
					if (response.incremental.length) {
						// Relay is expecting the contents of this array rather than the entire array itself. Also, Relay doesn't really care about tracking
						// the pending versus completed values, so we can just return the array without the extra account keeping.
						// @ts-expect-error It's hard to type this correctly because the payload sometimes has errors, sometimes not. Relay accounts for it,
						// TypeScript can't handle the polymorphism.
						payload = response.incremental;
					} else if (response.hasNext === false) {
						// It's possible for the server to send back an empty final increment.
						// Attempt to make Relay happy.
						payload = {
							data: null,
							extensions: { is_final: true },
						};
					}
				} else {
					// @ts-expect-error Typescript isn't happy that sometimes errors is undefined, but relay will manage just fine.
					payload = response;
				}

				// Notify observers that we have more data!
				if (payload) {
					sink.next(payload);

					// write cache query response on SSR
					if (__SERVER__ && requestID != null) {
						getQueryResponseCache().appendWithMetadata(
							requestID,
							variables,
							payload,
							loggingMetadata,
						);
					}
				}

				// Close the stream if there are no more responses.
				if ('hasNext' in response && response.hasNext === false) {
					finalise();
				}
			};

			// On client...
			if (!__SERVER__ && requestID != null) {
				// read cache from window.SPA_STATE if available
				const ssrCache = getQueryResponseCache().get(requestID, variables);

				if (!isNil(ssrCache)) {
					getQueryResponseCache().delete(requestID, variables);
					sink.next(ssrCache);
					if (isSsrHandoverIncrementsEnabled() && 'hasNext' in ssrCache && ssrCache.hasNext) {
						// TODO MAGMA-3017: what about SSR fallback mode, or scenarios where SSR has partially failed?
						// TODO MAGMA-3204: improve robustness. Should be using a middle-man module rather than client dispatcher directly.
						// Receive response events as they are streamed from the server.
						getClientDispatcher().register(sinkID, handleResponse);
						getClientDispatcher().onAbort(sinkID, ({ reason }) => {
							if (!sink.closed) {
								// We didn't receive everything!
								const err = new HttpError(424, `Incomplete handover for relay fetch: ${reason}`);
								err.name = 'ServerToClientError';
								loggingAndMetrics.logError(err);
								sink.error(err, false);
							}
						});
					} else {
						finalise();
					}
					return;
				}
			}

			const endpoint = getAggEndpoint(request, cacheConfig);
			const body = getRequestBody(request, variables);
			const fetchOverNetwork = buildGraphqlFetch(endpoint, generateOptions({ body }, endpoint));

			const initialSource = () =>
				!__SERVER__ && earlyScriptPromise
					? withErrorHandling(earlyScriptPromise)
					: withErrorHandling(fetchOverNetwork());

			const responses = processMultipartPromise(() =>
				retryOnError(initialSource, {
					retryFunc: () => withErrorHandling(fetchOverNetwork()),
					retryPredicate: shouldRetry,
					retryAttempts: getRetryAttemptsForQuery(queryName, operationKind),
					onRetry: loggingAndMetrics.logErrorRetry,
				}),
			);

			// Recreate a bizarre behaviour that will cause the observable to hang indefinitely when the user is not authenticated, presumably because we redirect the user to log in...
			// This should be revised; see https://jplat.atlassian.net/browse/MAGMA-3087
			let responseShouldHang = false;

			let firstResponseUsed = false;
			let resolveFirstResponse: () => void;
			const firstResponse: Promise<void> = new Promise((resolve) => {
				resolveFirstResponse = resolve;
			});

			responses.subscribe({
				start: () => {
					if (requestID != null) {
						if (__SERVER__) {
							// so that SSR SPA knows it is safe to render
							const renderAfterThisResponse = isSsrHandoverIncrementsEnabled()
								? firstResponse
								: completedResponse;
							QueryPromisesMap.set(requestID, renderAfterThisResponse);
						} else {
							QueryPromisesMap.set(requestID, firstResponse);
						}
						CompletedQueryPromisesMap.set(requestID, completedResponse);
					}
				},
				next: (response) => {
					if (__SERVER__ && isSsrHandoverIncrementsEnabled() && firstResponseUsed) {
						// Send the response payload to a side-channel.
						// TODO MAGMA-3204: improve robustness. Should be sending ID of module to communicate with, sinkID should be an argument.
						registerEvent({
							id: sinkID,
							name: 'default',
							args: [response],
						});

						if ('hasNext' in response && response.hasNext === false) {
							finalise();
						}

						// Don't send any further responses to the fetch subscriber, since we'll be sending them through a side-channel.
						return;
					}

					if (!firstResponseUsed) {
						firstResponseUsed = true;
						// according to spec, we can assume the first response is a GraphQLSingularResponse.
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						const singularResponse = response as GraphQLSingularResponse;

						augmentResponse(singularResponse);
						if (!validateResponse(singularResponse)) {
							//  Short circuit operation and handle the unauthorized errors.
							showUnauthenticatedFlag(queryName);
							responseShouldHang = true;
							return;
						}
						enforceNonNullability(singularResponse);
					}

					handleResponse(response);

					// signal that the initial response has been received
					resolveFirstResponse();

					// After the initial response, further responses are sent through a side-channel.
					if (__SERVER__ && isSsrHandoverIncrementsEnabled()) {
						sink.complete();
					}
				},
				error: (error: Error) => {
					if (!firstResponseUsed) {
						firstResponseUsed = true;
						// todo MAGMA-3172: check control flows for fetch failure cases on server + client. Seems weird that
						// the previous construction of the function would not send a signal that the response was received
						// in the error case...
						// resolveFirstResponse();
					}
					loggingAndMetrics.logError(error);
					sink.error(error);
				},
				complete: () => {
					if (responseShouldHang) return;
					finalise();
				},
			});
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			loggingAndMetrics.logError(error);
			sink.error(error);
		}
	});
};
